import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef } from 'react';
import { ChevronDown, HelpCircle } from 'lucide-react';

const FAQ = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.2 });
  const [openItems, setOpenItems] = useState(new Set([0])); // First item open by default

  const toggleItem = (index) => {
    const newOpenItems = new Set(openItems);
    if (newOpenItems.has(index)) {
      newOpenItems.delete(index);
    } else {
      newOpenItems.add(index);
    }
    setOpenItems(newOpenItems);
  };

  const faqs = [
    {
      question: "Quelles sont les conditions pour un accouchement en plateau technique ?",
      answer: "Un accouchement en plateau technique nécessite une grossesse à bas risque, sans complications médicales. Je procède à une évaluation complète de votre situation lors de nos consultations prénatales. En cas de pathologie ou de complications en cours de grossesse ou d'accouchement vous serez transférée vers l'équipe du Centre Hospitalier Annecy Genevois."
    },
    {
      question: "Comment se déroule le suivi de grossesse ?",
      answer: "Le suivi comprend des consultations mensuelles. Chaque consultation inclut un examen clinique, la surveillance de la croissance fœtale, des analyses si nécessaire, et nous prenons le temps de discuter de vos questions et préoccupations. Je vous informe sur le déroulement de votre grossesse, sur les ressources dont vous pourriez avoir besoin et vous reçois pour les séances de préparation à la naissance."
    },
    {
      question: "Que comprend l'accompagnement post-partum ?",
      answer: "L'accompagnement post-partum inclut des visites à domicile dans les premiers jours après la naissance, le suivi de votre récupération physique et émotionnelle, l'aide à l'allaitement maternel, les soins du nouveau-né (surveillance du poids et du risque d'ictère) et un soutien psychologique."
    },
    {
      question: "Quels sont vos tarifs et remboursements ?",
      answer: "Mes consultations sont facturées selon les tarifs conventionnés de la Sécurité Sociale. Je vous fournis tous les détails lors de notre premier rendez-vous."
    },
    {
      question: "Comment se déroule la préparation à la naissance orientée physiologique ?",
      answer: "Je propose des séances de préparation personnalisées incluant des exercices de respiration, de relaxation, des positions pour l'accouchement, et l'implication du partenaire. Nous abordons aussi les aspects pratiques et émotionnels de la naissance, et j'adapte la préparation selon vos besoins."
    },
    {
      question: "À partir de quand débuter le suivi ?",
      answer: "Vous pouvez me contacter dès que vous apprenez votre grossesse ou plus tard quand votre projet de naissance se précise."
    },
    {
      question: "Travaillez-vous avec d'autres professionnels ?",
      answer: "Je travaille en binôme avec Adrien Vallet afin d'assurer la continuité de votre prise en charge mais je travaille également en réseau."
    }
  ];

  return (
    <section id="faq" className="py-20 lg:py-32 bg-gradient-to-br from-sage-50 to-warm-50">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={isInView ? { opacity: 1, scale: 1 } : {}}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="flex justify-center mb-6"
          >
            <div className="bg-primary-100 p-4 rounded-full">
              <HelpCircle className="w-8 h-8 text-primary-600" />
            </div>
          </motion.div>

          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="font-display text-4xl lg:text-5xl font-bold text-sage-800 mb-6"
          >
            Questions fréquentes
          </motion.h2>
          
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.6 }}
            className="text-xl text-sage-600 leading-relaxed"
          >
            Retrouvez ici les réponses aux questions les plus fréquemment posées 
            sur mon accompagnement et mes services.
          </motion.p>
        </motion.div>

        {/* FAQ Items */}
        <div className="space-y-4">
          {faqs.map((faq, index) => {
            const isOpen = openItems.has(index);
            return (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.6, delay: 0.8 + index * 0.1 }}
                className="glass-card rounded-2xl overflow-hidden shadow-lg"
              >
                <button
                  onClick={() => toggleItem(index)}
                  className="w-full px-6 py-6 text-left focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 transition-all duration-200"
                >
                  <div className="flex items-center justify-between">
                    <h3 className="font-semibold text-lg text-sage-800 pr-8 leading-relaxed">
                      {faq.question}
                    </h3>
                    <motion.div
                      animate={{ rotate: isOpen ? 180 : 0 }}
                      transition={{ duration: 0.3 }}
                      className="flex-shrink-0"
                    >
                      <ChevronDown className="w-6 h-6 text-primary-600" />
                    </motion.div>
                  </div>
                </button>

                <AnimatePresence>
                  {isOpen && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3, ease: "easeInOut" }}
                      className="overflow-hidden"
                    >
                      <div className="px-6 pb-6">
                        <div className="pt-4 border-t border-sage-200">
                          <p className="text-sage-600 leading-relaxed">
                            {faq.answer}
                          </p>
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            );
          })}
        </div>

        {/* Contact CTA */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 1.6 }}
          className="text-center mt-16"
        >
          <p className="text-sage-600 mb-6 text-lg">
            Vous avez d'autres questions ?
          </p>
          <motion.a
            href="#contact"
            whileHover={{ scale: 1.05, y: -2 }}
            whileTap={{ scale: 0.95 }}
            className="inline-block bg-primary-600 hover:bg-primary-700 text-white px-8 py-4 rounded-full font-semibold shadow-lg hover:shadow-xl transition-all duration-300"
          >
            Contactez-moi
          </motion.a>
        </motion.div>
      </div>
    </section>
  );
};

export default FAQ;